import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, TrendingUp, Clock, Users, Building2, MapPin, Search, Bot, Layers, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback case studies if API fails or is empty
const fallbackCaseStudies = [
  {
    id: 'bc-cpa-firm',
    slug: 'bc-cpa-firm',
    title: 'Sustainable Growth by Design: How a Lower Mainland Accounting Firm Built a Steady Pipeline of Qualified Leads',
    client_name: 'CPA Firm in BC',
    industry: 'Professional Services',
    location: 'Lower Mainland, BC',
    engagement: 'Website modernization, local marketing, and AI-search optimization',
    challenge: "The client is a chartered professional accounting firm serving small and mid-sized businesses across the Lower Mainland, BC, with deep specialization in construction, transportation and logistics, restaurants, agriculture, and other B2B sectors. The firm had built a strong reputation through referrals and long-standing client relationships, and was ready to bring that same strength to its digital presence. The website hadn't yet been structured to capture that opportunity. It didn't clearly speak to the specific industries the firm serves, wasn't structured for local search visibility across the Lower Mainland, and gave AI-powered search tools like Google AI Overviews and ChatGPT little to work with. The brief was direct: modernize the website, build brand awareness across the region, and turn that visibility into a steady, sustainable stream of qualified leads.",
    solution: "The engagement combined three work streams, run together rather than sequentially, so local visibility, paid and organic traffic, and AI-search readiness reinforced each other instead of competing for priority.",
    workStreams: [
      {
        title: 'Local Marketing Strategy',
        icon: 'MapPin',
        points: [
          "Rebuilt and optimized the firm's Google Business Profiles across the Lower Mainland",
          'Built dedicated service and industry pages (construction, transportation, restaurants, agriculture, and other professionals) so each vertical had a page that spoke directly to its needs, the single strongest local organic ranking factor for professional services firms',
          'Layered in geo-targeted local content to capture high-intent "near me" and city-specific search behavior across the region'
        ]
      },
      {
        title: 'Organic and Paid Search Deployment',
        icon: 'Search',
        points: [
          'Mapped target keywords to funnel stage and rolled out organic content against them, backed by paid search campaigns for the highest-intent terms',
          "Prioritized bottom-funnel pages (services, pricing context, and industry comparisons) since these convert fastest once they're bringing in visitors"
        ]
      },
      {
        title: 'AI-Search-Optimized Website Rebuild',
        icon: 'Bot',
        points: [
          'Restructured every core page to lead with a direct answer, so both readers and AI answer engines get the key point in the first sentence',
          'Rewrote headers as the actual questions B2B buyers ask ("How much does a CPA cost for a construction company?") instead of generic marketing copy',
          "Added FAQ sections and structured schema markup to make the firm's content easy for AI systems to extract and cite, rather than just easy for Google to rank"
        ]
      }
    ],
    results: [
      '13% cumulative month-over-month growth in qualified leads (from month 4)',
      '9% month-over-month improvement in cost per lead',
      'Sustainable growth curve built on compounding local visibility and organic authority, not a short-lived spike',
      'Now quotable at the exact moment a Lower Mainland business owner is comparing accountants, on Google, in AI Overviews, or inside a chatbot conversation',
      'Three channels aligned: local, organic, and AI search'
    ],
    metrics: { lead_growth: '+13% MoM', cost_per_lead: '-9% MoM', channels_aligned: '3' },
    tech_stack: ['Local SEO', 'AEO / GEO', 'Google Business Profile', 'Schema Markup', 'FAQ Structured Data', 'Paid Search', 'Industry Landing Pages'],
    image_url: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200',
    testimonial: null,
    testimonial_author: null,
    featured: true,
    home_featured: true,
    industry_specialization: 'Serves small and mid-sized businesses across construction, transportation & logistics, restaurants, agriculture, and other B2B sectors.'
  },
  {
    id: 'ottawa-nutrition-b2b',
    slug: 'ottawa-nutrition-b2b',
    title: 'Building a Repeatable B2B Motion: How an Ottawa Nutrition Consulting Firm Grew a Steady Corporate Pipeline',
    client_name: 'Ottawa Nutrition Consulting Firm',
    industry: 'Wellness & Consulting',
    location: 'Ottawa, ON',
    engagement: 'B2B solution packaging, national lead generation, content strategy, and paid social',
    challenge: "The client is a nutritionist-led consulting practice based in Ottawa, Ontario, built on strong one-to-one client work. With that foundation in place, the firm was ready to expand into the corporate wellness market, selling nutrition programs into companies across Canada rather than relying solely on individual sign-ups. Two opportunities stood out. First, the firm's services were structured around individual consultations, and hadn't yet been packaged into a corporate wellness solution that an HR or benefits buyer could evaluate and purchase directly. Second, there was significant untapped potential in social media, which matters more in B2B wellness than it might seem: corporate buyers routinely check a consultant's social presence and content as an informal trust signal before ever taking a call.",
    solution: "The engagement was built around four connected work streams, packaging the offer first so leads and content would have a clear, credible destination.",
    workStreams: [
      {
        title: 'Custom B2B Solution Packages',
        icon: 'Layers',
        points: [
          "Packaged the firm's nutrition expertise into defined corporate wellness offers: clear scope, deliverables, and pricing structure that an HR or benefits buyer could evaluate without a discovery call",
          'Built packages around common corporate triggers (employee wellness budgets, benefits renewals, return-to-office health initiatives) so the offer matched how buyers actually plan spend'
        ]
      },
      {
        title: 'Lead Generation Plan Across Canada',
        icon: 'Search',
        points: [
          "Built a national outreach and inbound plan targeting HR, benefits, and wellness decision-makers across Canadian markets, extending beyond the firm's Ottawa home base",
          'Prioritized channels and messaging by province/sector to reflect where corporate wellness budgets were most active'
        ]
      },
      {
        title: 'Content Strategy to Build Trust',
        icon: 'Bot',
        points: [
          'Developed a content strategy centered on visible nutrition expertise, the credibility signal B2B wellness buyers look for before a first call',
          "Built a consistent publishing cadence so the firm's expertise was easy to find and evaluate, not just claimed on a homepage"
        ]
      },
      {
        title: 'Social Media Ads for Brand Awareness',
        icon: 'MapPin',
        points: [
          "Ran targeted paid social campaigns to put the newly packaged B2B offer and content in front of corporate wellness buyers who weren't yet searching for the firm by name",
          'Used awareness-stage campaigns to build reach first, so the content strategy and lead gen plan had a larger, warmer audience to convert'
        ]
      }
    ],
    results: [
      '17% cumulative month-over-month growth in qualified leads (from month 2)',
      '11% month-over-month improvement in cost per lead',
      'A repeatable B2B motion built to keep compounding rather than depending on one-to-one referrals alone',
      'Packaged corporate wellness offer that an HR or benefits buyer can evaluate without a discovery call',
      'National lead pipeline across Canadian markets beyond the firm’s Ottawa home base'
    ],
    metrics: { lead_growth: '+17% MoM', cost_per_lead: '-11% MoM', work_streams: '4' },
    tech_stack: ['B2B Offer Packaging', 'National Lead Gen', 'Content Strategy', 'Paid Social', 'Corporate Wellness Positioning'],
    image_url: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1200',
    testimonial: null,
    testimonial_author: null,
    featured: true,
    home_featured: true,
    industry_specialization: 'Nutritionist-led consulting practice expanding from one-to-one client work into corporate wellness programs across Canada.'
  },
  {
    id: 'local-deals-marketplace-app',
    slug: 'local-deals-marketplace-app',
    title: 'Growing Both Sides at Once: How a Canadian Local Deals App Scaled Vendors and Downloads Together',
    client_name: 'Local Deals & Rewards App',
    industry: 'Consumer App / Marketplace',
    location: 'Canada',
    engagement: 'Go-to-market strategy, B2B vendor onboarding lead generation, and B2C app download acquisition across Canada',
    challenge: "The client operates a local deals and rewards app connecting Canadian consumers with nearby merchants, letting shoppers discover, follow, and redeem offers from their favorite local businesses. The product itself was ready, but a two-sided marketplace like this only works once both sides are moving: consumers need enough vendors publishing deals to make the app worth opening, and vendors need enough app users redeeming offers to make onboarding worth their time. Without a coordinated go-to-market plan, that created a classic cold-start challenge. Growing vendors and growing downloads needed two different strategies, aimed at two very different audiences, but they also needed to reinforce each other rather than run as separate efforts.",
    solution: "Blue Ribbon Technologies built a go-to-market strategy with two connected lead engines: one bringing vendors onto the platform, and one bringing consumers onto the app, designed from the start to feed each other.",
    workStreams: [
      {
        title: 'B2B Vendor Onboarding',
        icon: 'Search',
        points: [
          'Built a targeted lead generation approach to identify and reach local merchants, prioritizing categories and neighborhoods where consumer demand was expected to be strongest',
          'Streamlined the onboarding path so a merchant could go from first contact to a live listing quickly, reducing the friction that typically stalls vendor-side growth'
        ]
      },
      {
        title: 'B2C Download Acquisition',
        icon: 'MapPin',
        points: [
          'Built an acquisition plan to drive app downloads across Canada, aimed at consumers most likely to become regular users of local deals',
          "Layered organic, paid, and referral channels together so download growth wasn't dependent on any single source"
        ]
      },
      {
        title: 'Referral Loop (Vendors ↔ Consumers)',
        icon: 'Bot',
        points: [
          'Encouraged onboarded vendors to promote the app in-store and to their own customer base, turning newly onboarded merchants into a downloads channel in their own right',
          'Closed the loop: more vendors meant more reasons for consumers to download, and more downloads made vendor onboarding conversations easier to win'
        ]
      }
    ],
    results: [
      '150+ vendors onboarded within the first 3 to 4 months',
      '300% growth in app downloads in the first quarter',
      '~35% of downloads driven by vendor referral and in-store promotion',
      'Day-one content depth across categories and neighborhoods from the vendor supply base',
      'A repeatable playbook for growing supply and demand together in any new market'
    ],
    metrics: { vendors_onboarded: '150+', download_growth: '+300%', referral_share: '35%' },
    tech_stack: ['Go-to-Market Strategy', 'B2B Lead Generation', 'App Store Acquisition', 'Referral Loops', 'Paid Social', 'Organic Growth'],
    image_url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200',
    testimonial: null,
    testimonial_author: null,
    featured: true,
    industry_specialization: 'A local deals and rewards marketplace app connecting Canadian consumers with nearby merchants across categories and neighborhoods.'
  },
  {
    id: 'canadian-cruise-booking',
    slug: 'canadian-cruise-booking',
    title: 'From Static to Sailing: How a Canadian Cruise Booking Company Built a High-Converting Digital Experience',
    client_name: 'Canadian Cruise & Vacation Booking Co.',
    industry: 'Travel & Cruise Booking',
    location: 'Canada',
    engagement: 'Dynamic website design, integrated booking engine, a conversational booking assistant, and Meta Ads management',
    challenge: "The client is a cruise and vacation booking company serving Canadian travelers, specializing in popular, luxury, and river cruise experiences. The business had real expertise in planning cruises, but its digital presence hadn't yet been built to turn that expertise into bookings. The website was running on largely static content, without a structured content strategy behind it, and the layout wasn't holding visitor attention or guiding them toward a next step. On the paid side, Meta Ads were running, but cost per click was high and the volume of leads coming through was low, a sign that targeting, creative, and the on-site experience receiving that traffic all had room to work harder together. The opportunity was clear: give visitors a website worth exploring, make booking a cruise simple to start, and get more out of every ad dollar already being spent.",
    solution: "Blue Ribbon Technologies combined four connected work streams, so the website, booking flow, and paid acquisition all reinforced each other instead of any one piece carrying the load alone.",
    workStreams: [
      {
        title: 'Dynamic Customer-Facing Website',
        icon: 'Bot',
        points: [
          'Replaced static, one-size-fits-all pages with dynamic content built around how travelers actually browse cruises — by destination, cruise type, and experience level',
          'Redesigned the layout for engagement, with clearer visual storytelling around popular, luxury, and river cruise options and a stronger path from browsing to inquiry'
        ]
      },
      {
        title: 'Integrated Booking Engine',
        icon: 'Layers',
        points: [
          'Connected a booking engine directly into the website so travelers could move from exploring a cruise to starting a booking without leaving the site or waiting on a callback'
        ]
      },
      {
        title: 'Conversational Booking Assistant',
        icon: 'Search',
        points: [
          'Added a conversational bot to answer common cruise questions and guide visitors toward the right itinerary in real time',
          'Captured interest that would otherwise leave the site unanswered'
        ]
      },
      {
        title: 'Meta Ads Management',
        icon: 'MapPin',
        points: [
          'Rebuilt targeting to reach travelers actively researching cruise vacations, rather than a broad travel audience',
          'Refreshed ad creative and copy around the destinations and cruise types generating the most on-site interest, improving click-through rate and return on ad spend without simply increasing budget'
        ]
      }
    ],
    results: [
      '38% organic traffic growth within 2 months of the new website going live',
      '225% growth in qualified leads within the quarter',
      '2-3 minute average traffic session duration',
      'A website built to convert, backed by a booking engine that removes friction from getting started',
      'Meta Ads reaching a sharper, higher-intent audience — improving efficiency on both sides of the funnel'
    ],
    metrics: { organic_traffic: '+38%', lead_growth: '+225%', session_duration: '2-3 min' },
    tech_stack: ['Dynamic Website Design', 'Integrated Booking Engine', 'Conversational AI Assistant', 'Meta Ads Optimization', 'Travel Content Strategy'],
    image_url: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=1200',
    testimonial: null,
    testimonial_author: null,
    featured: true,
    home_featured: true,
    industry_specialization: 'Cruise and vacation booking company specializing in popular, luxury, and river cruises for Canadian travelers.'
  }
];

export const CaseStudiesPage = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchCaseStudies = async () => {
      // Always start with fallback (curated studies) so featured content is guaranteed visible.
      // Merge with DB records; de-dupe by slug or id. DB records with a matching slug/id take precedence.
      const mergeStudies = (apiStudies = []) => {
        const bySlug = new Map();
        // Seed with fallback first
        fallbackCaseStudies.forEach(cs => bySlug.set(cs.slug || cs.id, cs));
        // API records override / add
        apiStudies.forEach(cs => {
          const key = cs.slug || cs.id;
          bySlug.set(key, { ...bySlug.get(key), ...cs });
        });
        return Array.from(bySlug.values());
      };
      try {
        const response = await axios.get(`${API_URL}/case-studies`);
        setCaseStudies(mergeStudies(response.data || []));
      } catch (error) {
        setCaseStudies(fallbackCaseStudies);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudies();
  }, []);

  const industries = ['all', ...new Set(caseStudies.map(cs => cs.industry))];
  const filteredStudies = filter === 'all' 
    ? caseStudies 
    : caseStudies.filter(cs => cs.industry === filter);

  return (
    <div data-testid="case-studies-page">
      {/* Hero */}
      <section className="section-padding bg-[#0A2540]">
        <div className="container-custom text-center">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Case Studies</span>
          <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Real Results for Real Businesses
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            See how Canadian SMBs are transforming their operations with our AI solutions
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-slate-100">
        <div className="container-custom">
          <div className="flex flex-wrap gap-3 justify-center">
            {industries.map((industry) => (
              <button
                key={industry}
                onClick={() => setFilter(industry)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === industry
                    ? 'bg-[#2563EB] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${industry}`}
              >
                {industry === 'all' ? 'All Industries' : industry}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-slate-500">Loading case studies...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {filteredStudies.map((study, index) => (
                <Link
                  key={study.id}
                  to={`/case-studies/${study.slug || study.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all"
                  data-testid={`case-study-card-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={study.image_url || 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=800'}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-xs font-medium text-[#2563EB] bg-[#2563EB]/10 px-3 py-1 rounded-full">
                        {study.industry}
                      </span>
                      {study.location && (
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-3 py-1 rounded-full inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {study.location}
                        </span>
                      )}
                    </div>
                    <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-2 line-clamp-2 group-hover:text-[#2563EB] transition-colors">
                      {study.title}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">{study.client_name}</p>
                    <div className="flex items-center gap-4 pt-4 border-t border-slate-100 flex-wrap">
                      {study.metrics && Object.entries(study.metrics).slice(0, 2).map(([k, v]) => (
                        <div key={k}>
                          <span className="text-lg font-bold text-[#2563EB]">{v}</span>
                          <span className="text-xs text-slate-500 ml-1 capitalize">{k.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
        <div className="container-custom text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
            Want Results Like These?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Let's discuss how we can achieve similar transformations for your business.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-8 py-6 text-lg">
              Start Your Transformation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// Helper: get case study by id or slug (for public consumption from HomePage)
export const getFeaturedCaseStudies = (limit = 2) => {
  const homeFeatured = fallbackCaseStudies.filter(cs => cs.home_featured);
  if (homeFeatured.length > 0) return homeFeatured.slice(0, limit);
  return fallbackCaseStudies.filter(cs => cs.featured).slice(0, limit);
};

export const CaseStudyDetailPage = () => {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axios.get(`${API_URL}/case-studies/${id}`);
        setCaseStudy(response.data);
      } catch (error) {
        // Try fallback by id OR slug
        const fallback = fallbackCaseStudies.find(cs => cs.id === id || cs.slug === id);
        if (fallback) {
          setCaseStudy(fallback);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudy();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="section-padding text-center" data-testid="case-study-not-found">
        <h1 className="font-outfit text-3xl font-bold text-[#0A2540] mb-4">Case Study Not Found</h1>
        <Link to="/case-studies">
          <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">
            View All Case Studies
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="case-study-detail-page">
      {/* Hero */}
      <section className="section-padding bg-[#0A2540]">
        <div className="container-custom">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Case Studies
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="text-sm font-medium text-[#2563EB] bg-[#2563EB]/20 px-3 py-1 rounded-full">
                  {caseStudy.industry}
                </span>
                {caseStudy.location && (
                  <span className="text-sm font-medium text-white/80 bg-white/10 px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {caseStudy.location}
                  </span>
                )}
              </div>
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                {caseStudy.title}
              </h1>
              <p className="text-white/70 text-lg">{caseStudy.client_name}</p>
              {caseStudy.industry_specialization && (
                <p className="text-white/50 text-sm mt-3 leading-relaxed">{caseStudy.industry_specialization}</p>
              )}
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={caseStudy.image_url || 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=800'}
                alt={caseStudy.title}
                className="w-full aspect-video object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Metrics */}
      {caseStudy.metrics && (
        <section className="py-12 bg-white border-b border-slate-100">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {Object.entries(caseStudy.metrics).map(([key, value], index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-[#2563EB]">{value}</div>
                  <div className="text-sm text-slate-500 capitalize">{key.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-14">
            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-bold text-[#0A2540] mb-4">The Challenge</h2>
              <p className="text-slate-600 text-lg leading-relaxed">{caseStudy.challenge}</p>
            </div>

            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-bold text-[#0A2540] mb-4">Our Solution</h2>
              <p className="text-slate-600 text-lg leading-relaxed">{caseStudy.solution}</p>

              {/* Work Streams (structured multi-track solution) */}
              {caseStudy.workStreams && caseStudy.workStreams.length > 0 && (
                <div className={`mt-10 grid grid-cols-1 gap-6 ${caseStudy.workStreams.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
                  {caseStudy.workStreams.map((ws, i) => {
                    const iconMap = { MapPin, Search, Bot, Layers };
                    const Icon = iconMap[ws.icon] || Layers;
                    return (
                      <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-testid={`workstream-${i}`}>
                        <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-xl flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Work stream {i + 1}</div>
                        <h3 className="font-outfit text-lg font-semibold text-[#0A2540] mb-3 leading-tight">{ws.title}</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          {ws.points.map((p, j) => (
                            <li key={j} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-[#2563EB] mt-0.5 flex-shrink-0" />
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-bold text-[#0A2540] mb-4">Results</h2>
              <ul className="space-y-3">
                {caseStudy.results.map((result, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-1" />
                    <span className="text-slate-600 text-lg">{result}</span>
                  </li>
                ))}
              </ul>
            </div>

            {caseStudy.tech_stack && caseStudy.tech_stack.length > 0 && (
              <div>
                <h2 className="font-outfit text-2xl md:text-3xl font-bold text-[#0A2540] mb-4">Approach & Tech Stack</h2>
                <div className="flex flex-wrap gap-2">
                  {caseStudy.tech_stack.map((t, i) => (
                    <span key={i} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {caseStudy.testimonial && (
              <div className="bg-slate-50 p-8 rounded-2xl border-l-4 border-[#2563EB]">
                <p className="text-xl text-[#0A2540] italic mb-4 leading-relaxed">"{caseStudy.testimonial}"</p>
                <p className="text-slate-500 text-sm">, {caseStudy.testimonial_author}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
        <div className="container-custom text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
            Ready for Similar Results?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Let's discuss how we can help transform your business.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-8 py-6 text-lg">
              Book a Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
